import React, { useState, useRef } from 'react';
import MonacoEditor from '@monaco-editor/react';

interface ImportPolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
  importPolicy: (raw: unknown) => { success: true } | { success: false; errors: string[] };
}

export const ImportPolicyModal: React.FC<ImportPolicyModalProps> = ({ isOpen, onClose, importPolicy }) => {
  // Always use dark theme
  const colorScheme = 'dark';
  const [jsonText, setJsonText] = useState('');
  const [errors, setErrors] = useState<string[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    processFile(file);
  };

  const processFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      const text = reader.result as string;
      setJsonText(text);
      setErrors([]); // Clear previous errors when new content is loaded
      // Reset file input to allow selecting the same file again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    };
    reader.readAsText(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type === 'application/json') {
      processFile(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleImport = () => {
    if (!jsonText.trim()) {
      setErrors(['Please provide JSON content to import.']);
      return;
    }

    const result = importPolicy(jsonText);
    if (result.success) {
      setErrors([]);
      setJsonText('');
      // Reset file input when import is successful
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      onClose();
    } else {
      const guidance = 'Ensure your JSON includes at least one of: mode, policyRule, or parameters at the root, or wraps them under a properties object.';
      setErrors([...result.errors, guidance]);
    }
  };

  const clearEditor = () => {
    setJsonText('');
    setErrors([]);
    // Reset file input when clearing editor
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-7xl h-[95vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Import Azure Policy</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">Import from JSON file or paste content directly</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Main Content - Using grid instead of flex for better control */}
        <div className="flex-1 grid grid-cols-[400px_1fr] overflow-hidden">
          {/* Left Panel - File Upload */}
          <div className="p-6 border-r border-gray-200 dark:border-gray-700 max-w-none">
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Upload File</h3>
                <div
                  className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                    isDragOver
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                  }`}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                >
                  <div className="space-y-2">
                    <div className="mx-auto w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Drop your JSON file here, or{' '}
                      <label className="text-blue-600 dark:text-blue-400 hover:underline cursor-pointer">
                        browse
                        <input
                          type="file"
                          accept=".json,application/json"
                          className="hidden"
                          onChange={handleFileChange}
                          ref={fileInputRef}
                        />
                      </label>
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-500">Supports .json files</p>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white">Quick Actions</h4>
                <div className="space-y-2">
                  <button
                    onClick={clearEditor}
                    className="w-full flex items-center justify-center space-x-2 px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    <span>Clear Editor</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Right Panel - JSON Editor */}
          <div className="flex flex-col min-w-0">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">Policy JSON</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Paste or edit your Azure Policy JSON content</p>
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {jsonText.length > 0 && `${jsonText.length} characters`}
                </div>
              </div>
            </div>

            <div className="flex-1 min-h-0">
              <MonacoEditor
                height="100%"
                defaultLanguage="json"
                value={jsonText}
                onChange={(value) => {
                  setJsonText(value || '');
                  if (errors.length > 0) setErrors([]); // Clear errors when user starts typing
                }}
                theme={colorScheme === 'dark' ? 'vs-dark' : 'light'}
                options={{
                  minimap: { enabled: false },
                  formatOnType: true,
                  formatOnPaste: true,
                  automaticLayout: true,
                  wordWrap: 'on',
                  lineNumbers: 'on',
                  scrollBeyondLastLine: false,
                  fontSize: 13,
                }}
                onMount={(_editor, monaco) => {
                  // Monaco 0.53+ exposes jsonDefaults at languages.jsonDefaults; older path is languages.json.jsonDefaults
                  const jsonDefaults =
                    (monaco.languages as unknown as { jsonDefaults?: { setDiagnosticsOptions: Function }; json?: { jsonDefaults?: { setDiagnosticsOptions: Function } } })
                      .jsonDefaults ||
                    (monaco.languages as unknown as { json?: { jsonDefaults?: { setDiagnosticsOptions: Function } } }).json?.jsonDefaults;

                  jsonDefaults?.setDiagnosticsOptions({
                    validate: true,
                    allowComments: false,
                    schemas: [],
                  });
                }}
              />
            </div>
          </div>
        </div>

        {/* Error Display */}
        {errors.length > 0 && (
          <div className="p-3 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-red-800 dark:text-red-200 mb-1">Import Errors</h4>
                  <div className="text-sm text-red-700 dark:text-red-300 space-y-1">
                    {errors.map((err, idx) => (
                      <div key={idx} className="flex items-start space-x-2">
                        <span className="font-mono text-xs">•</span>
                        <span>{err}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <button
                  onClick={() => setErrors([])}
                  className="flex-shrink-0 p-1 text-red-400 hover:text-red-600 dark:hover:text-red-200 transition-colors rounded-lg hover:bg-red-100 dark:hover:bg-red-800/50"
                  title="Dismiss errors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Supports Azure Policy JSON format with automatic validation
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleImport}
              disabled={!jsonText.trim()}
              className="px-6 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              Import Policy
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportPolicyModal; 
